import { RightOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  Input,
  message,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaStarOfLife } from 'react-icons/fa6';
import { stackholderAccountSchema } from '../../Utils/Schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  createStackholderAccount,
  getLinkedAccount,
  getStackHolderAccount,
} from '../../Actions/RazorpaySplittingAction';

const StackholderAccount = ({ next, prev, activeKey, markStepComplete }) => {
  const dispatch = useDispatch();
  const [sameAsRegistered, setSameAsRegistered] = useState(false);
  const [registeredAddress, setRegisteredAddress] = useState(null);
  const {
    restaurant,
    allLinkedAccountData,
    allStackHolderData,
    isCreateStackholderAccountLoading,
    isGetLinkedAccountLoading,
  } = useSelector((state) => {
    const {
      allLinkedAccountData,
      allStackHolderData,
      isCreateStackholderAccountLoading,
      isGetLinkedAccountLoading,
    } = state.razorpaySplitting;
    const { restaurant } = state.restaurant;
    return {
      restaurant,
      allLinkedAccountData,
      allStackHolderData,
      isCreateStackholderAccountLoading,
      isGetLinkedAccountLoading,
    };
  });

  const {
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    control,
    reset,
    setValue,
    clearErrors,
    getValues,
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      street: '',
      city: '',
      state: '',
      postal_code: '',
      pan: '',
    },
    resolver: yupResolver(stackholderAccountSchema),
  });

  const disabledFormData =
    !allLinkedAccountData?.id || allStackHolderData?.items?.[0]?.id;
  const prevLoadingRef = useRef(isGetLinkedAccountLoading);

  useEffect(() => {
    const loadingJustFinished =
      prevLoadingRef.current === true && isGetLinkedAccountLoading === false;
    prevLoadingRef.current = isGetLinkedAccountLoading;

    if (
      activeKey === '1' &&
      loadingJustFinished &&
      !allLinkedAccountData?.id &&
      !restaurant?.payment_splitting?.linked_account_id
    ) {
      message.error('Please create linked account first');
    }
  }, [activeKey, isGetLinkedAccountLoading, allLinkedAccountData?.id]);

  useEffect(() => {
    dispatch(getStackHolderAccount());
    dispatch(
      getLinkedAccount((data) => {
        const linkedAccount = data?.profile?.addresses?.registered;
        if (linkedAccount) {
          setRegisteredAddress(linkedAccount);
        }
      })
    );
  }, []);

  useEffect(() => {
    if (allStackHolderData?.items?.length > 0) {
      const data = allStackHolderData.items[0];

      reset({
        name: data.name || '',
        email: data.email || '',
        street: data.addresses?.residential?.street || '',
        city: data.addresses?.residential?.city || '',
        state: data.addresses?.residential?.state || '',
        postal_code: data.addresses?.residential?.postal_code || '',
        pan: data.kyc?.pan || '',
      });
    }
  }, [allStackHolderData, reset]);

  const handleCreateStackholderAccount = (data) => {
    const hasValidOperationAddress =
      !!data.street &&
      !!data.city &&
      !!data.state &&
      !!data.postal_code &&
      !!data.country;
    const transformed = {
      stakeholder_account: {
        name: data.name,
        email: data.email,
        percentage_ownership: 100,
        ...(hasValidOperationAddress && {
          addresses: {
            residential: {
              street: data.street || null,
              city: data.city || null,
              state: data.state || null,
              postal_code: data.postal_code || null,
              country: data.country || null,
            },
          },
        }),

        kyc: {
          pan: data.pan || null,
        },
        notes: {
          random_key_by_partner: restaurant?.id || null,
        },
      },
    };
    dispatch(
      createStackholderAccount(
        transformed,
        successCreateStackholderAccount,
        failureCreateStackholderAccount
      )
    );
  };

  const successCreateStackholderAccount = (data) => {
    markStepComplete();
    dispatch(getStackHolderAccount());
    setTimeout(() => {
      message.success('Stack holder account created successfully');
    }, 1000);
    // next();
  };

  const failureCreateStackholderAccount = (error) => {
    let errorMessage =
      error?.error?.error?.description ||
      error?.error?.description ||
      error?.error?.error ||
      error?.error ||
      error?.message ||
      'Something went wrong.';

    if (typeof errorMessage === 'object') {
      errorMessage = errorMessage.error || JSON.stringify(errorMessage);
    }
    setTimeout(() => {
      message.error(errorMessage);
    }, 1000);
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setSameAsRegistered(checked);

    if (checked && registeredAddress) {
      const street = [registeredAddress.street1, registeredAddress.street2]
        .filter(Boolean)
        .join(' ')
        .trim();

      setValue('street', street || '');
      setValue('city', registeredAddress.city || '');
      setValue('state', registeredAddress.state || '');
      setValue('postal_code', registeredAddress.postal_code || '');
    } else {
      // Clear fields when unchecked
      setValue('street', '');
      setValue('city', '');
      setValue('state', '');
      setValue('postal_code', '');
    }
  };

  return (
    <form onSubmit={handleSubmit(handleCreateStackholderAccount)}>
      <Spin
        spinning={
          isCreateStackholderAccountLoading || isGetLinkedAccountLoading
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Typography className='formHeading'>
              Create a Stakeholder account
            </Typography>
          </Col>
          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Row gutter={[10, 16]}>
                  <Col span={24}>
                    <Typography
                      className='formHeading'
                      style={{ marginBottom: '-15px' }}
                    >
                      Name as per PAN card
                      <FaStarOfLife
                        style={{
                          fontSize: '7px',
                          color: '#eb5757',
                          position: 'absolute',
                          top: '7%',
                          marginLeft: '2px',
                        }}
                      />
                    </Typography>
                  </Col>
                  <Col span={24}>
                    <Controller
                      name='name'
                      control={control}
                      className='inputLabel'
                      as={<Input size='large' disabled={disabledFormData} />}
                    />
                    {errors.name && (
                      <p className='errorMessage'>{errors.name.message}</p>
                    )}
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row gutter={[10, 16]}>
                  <Col span={24}>
                    <Typography
                      className='formHeading'
                      style={{ marginBottom: '-15px' }}
                    >
                      Email
                      <FaStarOfLife
                        style={{
                          fontSize: '7px',
                          color: '#eb5757',
                          position: 'absolute',
                          top: '7%',
                          marginLeft: '2px',
                        }}
                      />
                    </Typography>
                  </Col>
                  <Col span={24}>
                    <Controller
                      name='email'
                      control={control}
                      className='inputLabel'
                      as={<Input size='large' disabled={disabledFormData} />}
                    />
                    {errors.email && (
                      <p className='errorMessage'>{errors.email.message}</p>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col>
                <Typography
                  className='formHeading'
                  style={{ paddingBottom: '10px' }}
                >
                  {' '}
                  Residential Address
                  <FaStarOfLife
                    style={{
                      fontSize: '7px',
                      color: '#eb5757',
                      position: 'absolute',
                      top: '1%',
                      marginLeft: '2px',
                    }}
                  />
                </Typography>
                <Checkbox
                  checked={sameAsRegistered}
                  onChange={handleCheckboxChange}
                  style={{ marginBottom: '10px' }}
                >
                  {' '}
                  <Typography className='formHeading'>
                    Same as Registered Address
                  </Typography>
                </Checkbox>
                <Row
                  gutter={[5, 16]}
                  style={{ border: '1px solid #ccc', padding: '10px' }}
                >
                  <Col span={24}>
                    <Row gutter={[10, 16]}>
                      <Col span={24}>
                        <Typography
                          className='formHeading'
                          style={{ marginBottom: '-15px' }}
                        >
                          Address
                        </Typography>
                      </Col>
                      <Col span={24}>
                        <Controller
                          name='street'
                          control={control}
                          className='inputLabel'
                          as={
                            <Input size='large' disabled={disabledFormData} />
                          }
                        />
                        {errors.street && (
                          <p className='errorMessage'>
                            {errors.street.message}
                          </p>
                        )}
                      </Col>
                    </Row>
                  </Col>

                  {/* <Col span={24}>
                    <Row gutter={[10, 16]}>
                      <Col span={24}>
                        <Typography
                          className='formHeading'
                          style={{ marginBottom: '-15px' }}
                        >
                          Address line 2
                        </Typography>
                      </Col>
                      <Col span={24}>
                        <Controller
                          name='phone'
                          control={control}
                          className='inputLabel'
                          as={<Input size='large' />}
                        />
                      </Col>
                    </Row>
                  </Col> */}
                  <Col span={24}>
                    <Row gutter={[10, 16]}>
                      <Col span={12}>
                        <Row gutter={[10, 16]}>
                          <Col span={24}>
                            <Typography
                              className='formHeading'
                              style={{ marginBottom: '-15px' }}
                            >
                              City
                            </Typography>
                          </Col>
                          <Col span={24}>
                            <Controller
                              name='city'
                              control={control}
                              className='inputLabel'
                              as={
                                <Input
                                  size='large'
                                  disabled={disabledFormData}
                                />
                              }
                            />
                            {errors.city && (
                              <p className='errorMessage'>
                                {errors.city.message}
                              </p>
                            )}
                          </Col>
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row gutter={[10, 16]}>
                          <Col span={24}>
                            <Typography
                              className='formHeading'
                              style={{ marginBottom: '-15px' }}
                            >
                              State
                            </Typography>
                          </Col>
                          <Col span={24}>
                            <Controller
                              name='state'
                              control={control}
                              className='inputLabel'
                              as={
                                <Input
                                  size='large'
                                  disabled={disabledFormData}
                                />
                              }
                            />
                            {errors.state && (
                              <p className='errorMessage'>
                                {errors.state.message}
                              </p>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Row gutter={[10, 16]}>
                      <Col span={12}>
                        <Row gutter={[10, 16]}>
                          <Col span={24}>
                            <Typography
                              className='formHeading'
                              style={{ marginBottom: '-15px' }}
                            >
                              Country
                            </Typography>
                          </Col>
                          <Col span={24}>
                            <Controller
                              name='country'
                              control={control}
                              style={{ width: '100%' }}
                              defaultValue='IN'
                              as={<Select size='large' disabled />}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row gutter={[10, 16]}>
                          <Col span={24}>
                            <Typography
                              className='formHeading'
                              style={{ marginBottom: '-15px' }}
                            >
                              Postal code
                            </Typography>
                          </Col>
                          <Col span={24}>
                            <Controller
                              name='postal_code'
                              control={control}
                              className='inputLabel'
                              as={
                                <Input
                                  size='large'
                                  disabled={disabledFormData}
                                />
                              }
                            />
                            {errors.postal_code && (
                              <p className='errorMessage'>
                                {errors.postal_code.message}
                              </p>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Row gutter={[10, 16]}>
              <Col span={24}>
                <Typography
                  className='formHeading'
                  style={{ marginBottom: '-15px' }}
                >
                  PAN number
                  <FaStarOfLife
                    style={{
                      fontSize: '7px',
                      color: '#eb5757',
                      position: 'absolute',
                      top: '7%',
                      marginLeft: '2px',
                    }}
                  />
                </Typography>
              </Col>
              <Col span={24}>
                <Controller
                  name='pan'
                  control={control}
                  className='inputLabel'
                  as={<Input size='large' disabled={disabledFormData} />}
                />
                {errors.pan && (
                  <p className='errorMessage'>{errors.pan.message}</p>
                )}
              </Col>
            </Row>
          </Col>
          {/* <Col span={24}>
            <Row gutter={[10, 16]}>
              <Col span={24}>
                <Typography
                  className='formHeading'
                  style={{ marginBottom: '-15px' }}
                >
                  Notes
                </Typography>
              </Col>
              <Col span={24}>
                <Controller
                  name='random_key_by_partner'
                  control={control}
                  className='inputLabel'
                  as={<TextArea size='large' />}
                />
              </Col>
            </Row>
          </Col> */}
        </Row>
        <Col span={24} style={{ marginTop: '40px' }}>
          <Row gutter={[16, 16]} className='button' justify='center'>
            <Col xl={6} lg={6} md={8} sm={12} xs={12}>
              <Button className='detailsButton' size='large' onClick={prev}>
                Previous
              </Button>
            </Col>
            <Col xl={6} lg={6} md={8} sm={12} xs={12}>
              <Button
                className={disabledFormData ? 'classButton' : 'detailsButton'}
                htmlType='submit'
                size='large'
                disabled={disabledFormData}
              >
                Create
              </Button>
            </Col>
            <Col xl={6} lg={6} md={8} sm={12} xs={12}>
              <Button size='large' className='detailsButton' onClick={next}>
                Next
              </Button>
            </Col>
          </Row>
        </Col>
      </Spin>
    </form>
  );
};

export default StackholderAccount;
